import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Copro } from '../../../@types/Copro';
import { isActivePaymentStatus } from '../../../@types/PaymentStatus';
import type { RnicTableData } from '../../../@types/RnicTableData';
import { updateCompanyBranding } from '../../../store/AuthReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { showToast } from '../Notification';
import Popup from '../Popup/Popup.module';
import { updateSetupState } from '../SetupPage';
import Spinner from '../Spinner';

const AdminAutoDemo: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isCompanyAlreadyConfigured, setIsCompanyAlreadyConfigured] = useState(false);
  const [checkingConfiguration, setCheckingConfiguration] = useState(false);
  const dispatch = useAppDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const isAdmin = useSelector((state: RootState) => state.auth.user?.roles?.includes('ROLE_ADMIN') || false);

  const checkCompanyAlreadyConfigured = useCallback(async(): Promise<boolean> => {
    if (!company?.id || !jwt) {return false;}

    // Check 1: Active membership status
    if (isActivePaymentStatus(company.paymentStatus)) {
      return true;
    }

    // Check 2: the copro validation setup step has been completed.
    // Do NOT probe for admin-validated copros here: copros are admin-validated on
    // creation by default, so `filterValidatedByAdmin=true` only means "this company
    // owns at least one copro" and would block the demo for untouched prospects.
    // `coproValidation` is set by the real validation step and by this demo itself.
    if (company.setupSteps?.coproValidation) {
      return true;
    }

    return false;
  }, [company, jwt]);

  const setAdminValidation = async(id: number, validatedByAdmin: boolean, token: string) => {
    let url = `/api/copros/${id}/`;
    url += validatedByAdmin ? 'accept' : 'refuse';
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(token),
      },
    });

    if (!response.ok) {
      console.error('Error setting admin validation :', response);
      throw new Error('Error setting admin validation');
    }
  };


  interface CoproBatchPayload {
    name: string;
    batchSize: string;
    type: number;
    remoteId: string;
    termEndAt: string;
    validatedByUser: boolean;
    generalMeetingApiDate?: string;
    addresses: Array<{ postalCode: string; address: string; city: string; remoteId: string }>;
  }

  const createCoprosBatch = async(copros: CoproBatchPayload[], companyId: number, token: string) => {
    try {
      const response = await fetch(`/api/companies/${companyId}/copros/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(token),
        },
        body: JSON.stringify(copros),
      });

      if (!response.ok) {
        throw new Error('Error creating copros batch');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating copros batch :', error);
      throw error;
    }
  };


  const processCoproData = async(data: Partial<RnicTableData>[], companyId: number, token: string) => {
    const coprosToBatch = [];
    const coprosToUpdate = [];
    data.forEach((copro) => {

      const payload = {
        name: copro.NOM,
        batchSize: copro['MIXTE HAB / PRO'],
        type: 1,
        remoteId: copro['N° IMMAT'],
        termEndAt: copro.mandatUnformatted,
        validatedByUser: true,
        generalMeetingApiDate: copro.generalMeetingApiDate,
        addresses: copro.ADRESSEBAN && copro.CP && copro.COMMUNE && copro.matchedBanIdAdr
          ? [{
            postalCode: copro.CP,
            address: copro.ADRESSEBAN,
            city: copro.COMMUNE,
            remoteId: copro.matchedBanIdAdr,
          }]
          : [],
      };

      if (!copro.coproId) {
        coprosToBatch.push(payload);
      } else {
        const updatePayload = { ...payload, generalMeetingDate: copro.generalMeetingDate };
        delete updatePayload.addresses;
        coprosToUpdate.push({ id: copro.coproId, payload: updatePayload });
      }
    });

    if (coprosToBatch.length > 0) {
      await createCoprosBatch(coprosToBatch, companyId, token);
    }

    // We do not want to update in demo mode, we should only handle new copro case for the demo
    // await Promise.all(coprosToUpdate.map(({ id, payload }) => updateCopro(id, payload, token)));
  };

  interface RnicApiItem {
    numeroDimmatriculation?: string;
    nomDusageDeLaCopropriete?: string;
    adresseDeReference?: string;
    banCodePostal?: string;
    matchedBanNomCom?: string;
    nombreTotalDeLots?: string;
    nombreTotalDeLotsAUsageDhabitationDeBureauxOuDeComme?: string;
    identificationDuRepresentantLegalRaisonSocialeEtLeNume?: string;
    matchedBanIdAdr?: string;
    dateDeFinDuDernierMandat?: string;
    detailsDonneesfinancieresDateag?: string;
    siretRepresentantLegal?: string;
    siretRepresentantLegalSiExiste?: string;
    matchedBanNumero?: string;
    matchedBanRep?: string;
    matchedBanNomVoie?: string;
  }

  const processRnicData = (rnicData: RnicApiItem[], existingCopros: Copro[]): Partial<RnicTableData>[] => {
    return rnicData.map((item) => {
      let ADRESSEBAN = '';
      if (item.matchedBanNumero) {
        ADRESSEBAN += item.matchedBanNumero;
      }
      if (item.matchedBanRep) {
        ADRESSEBAN += item.matchedBanRep;
      }
      if (item.matchedBanNomVoie) {
        ADRESSEBAN += ` ${item.matchedBanNomVoie}`;
      }
      const processedItem: Partial<RnicTableData> = {
        'N° IMMAT': item.numeroDimmatriculation ?? '',
        NOM: item.nomDusageDeLaCopropriete ?? '',
        ADRESSEBAN,
        CP: item.banCodePostal ?? '',
        COMMUNE: item.matchedBanNomCom ?? '',
        'NB LOTS': item.nombreTotalDeLots ? Number(item.nombreTotalDeLots) : 0,
        'MIXTE HAB / PRO': item.nombreTotalDeLotsAUsageDhabitationDeBureauxOuDeComme ?? '',
        'NOM DU SYNDIC DANS REGISTRE': item.identificationDuRepresentantLegalRaisonSocialeEtLeNume ?? '',
        matchedBanIdAdr: item.matchedBanIdAdr ?? '',
        mandatUnformatted: item.dateDeFinDuDernierMandat ?? '',
        VALIDE: true,
        generalMeetingApiDate: item.detailsDonneesfinancieresDateag ?? '',
        siretRepresentantLegal: item.siretRepresentantLegalSiExiste ?? item.siretRepresentantLegal ?? '',
      };

      const existingCopro = existingCopros.find(copro => copro.remoteId === processedItem['N° IMMAT']);
      if (existingCopro) {
        processedItem.VALIDE = existingCopro.validatedByUser;
        processedItem.coproId = existingCopro.id;
        processedItem.generalMeetingDate = existingCopro.generalMeetingDate != null ? String(existingCopro.generalMeetingDate) : '';
      }

      return processedItem;
    });
  };

  const handleDemoClick = async() => {
    if (!company || !jwt || !company.id) {
      showToast('error', 'Informations obligatoires manquantes.');
      return;
    }

    if (status !== 'idle') {
      return;
    }

    setShowConfirmation(true);
    setCheckingConfiguration(true);

    const alreadyConfigured = await checkCompanyAlreadyConfigured();
    setIsCompanyAlreadyConfigured(alreadyConfigured);
    setCheckingConfiguration(false);
  };

  const applyDemoBranding = async() => {
    if (!company?.id || !jwt) {
      throw new Error('Informations de société manquantes pour la configuration de la démo.');
    }

    const existingBranding = company.brandingSettings ?? {};
    const demoReferentId = `ref_demo_${Date.now()}`;
    const brandingSettingsPayload = {
      ...existingBranding,
      defaultTemplate: 'Degueldre',
      referents: [{
        id: demoReferentId,
        title: 'Madame',
        firstname: 'Josette',
        lastname: 'DURAND',
        email: '',
        phone: '',
        photoName: undefined, // Will be updated after photo upload
      }],
      emailSubcontractorName: 'EMAILGO',
    };

    const brandingResponse = await fetch(`/api/companies/${company.id}/branding-settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(jwt),
      },
      body: JSON.stringify({ brandingSettings: brandingSettingsPayload }),
    });

    if (!brandingResponse.ok) {
      const errorText = await brandingResponse.text().catch(() => '');
      throw new Error(errorText || 'Erreur lors de la mise à jour du branding.');
    }

    dispatch(updateCompanyBranding(brandingSettingsPayload));

    const uploadDemoAsset = async(assetPath: string, endpoint: string, fileName: string, extraFormData?: Record<string, string>) => {
      const assetResponse = await fetch(assetPath);

      if (!assetResponse.ok) {
        throw new Error(`Impossible de charger la ressource ${assetPath}.`);
      }

      const assetBlob = await assetResponse.blob();
      const rawContentType = assetBlob.type || assetResponse.headers.get('content-type') || undefined;
      const sanitizedContentType = rawContentType ? rawContentType.split(';')[0]?.trim() : undefined;
      const file = new File([assetBlob], fileName, { type: sanitizedContentType || 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', file);
      if (extraFormData) {
        Object.entries(extraFormData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      const uploadResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorPayload = await uploadResponse.text().catch(() => '');
        throw new Error(errorPayload || `Échec du téléchargement de ${fileName}.`);
      }

      return uploadResponse.json();
    };

    // Upload referent photo with referentId
    await uploadDemoAsset(
      '/demo-syndic/demo-referent.jpg',
      `/api/companies/${company.id}/referent-photo`,
      'demo-referent.jpg',
      { referentId: demoReferentId },
    );

    // Upload company logo
    await uploadDemoAsset(
      '/demo-syndic/demo-syndic.jpg',
      `/api/companies/${company.id}/branding-image`,
      'demo-syndic.jpg',
    );
  };

  const handleConfirmDemo = async() => {
    setShowConfirmation(false);
    setStatus('loading');

    try {
      // Step 0: Apply demo branding assets and identity
      await applyDemoBranding();

      // Step 1: Fetch RNIC data
      const rnicResponse = await fetch(`/api/companies/${company.id}/rnics`, JWTHeader(jwt));
      const rnicData = await rnicResponse.json();

      // Step 2: Fetch existing copros
      const coproResponse = await fetch(`/api/companies/${company.id}/copros?limit=100000`, JWTHeader(jwt));
      const coproData = await coproResponse.json();

      // Process and merge data
      const processedData = processRnicData(rnicData, coproData.results);

      // Step 3: Create or update copros
      await processCoproData(processedData, company.id, jwt);

      // Step 4: Update setup state
      await updateSetupState(company, jwt, 'coproValidation', dispatch);

      // Step 5: Validate copros as admin
      // Fetch all copros for the company
      const allCoprosResponse = await fetch(`/api/companies/${company.id}/copros?limit=100000`, JWTHeader(jwt));
      const allCoprosData = await allCoprosResponse.json();
      
      // Filter copros where siretRepresentantLegal matches company.siren
      const coprosToValidate = allCoprosData.results.filter(copro => 
        copro && copro.details && copro.details.siretRepresentantLegalSiExiste && copro.details.siretRepresentantLegalSiExiste.trim().startsWith(company.siren),
      );

      // Validate filtered copros in batches to avoid overwhelming the server
      const batchSize = 50;
      const batches = [];
      for (let i = 0; i < coprosToValidate.length; i += batchSize) {
        batches.push(coprosToValidate.slice(i, i + batchSize));
      }
      await batches.reduce(async(previousPromise, batch) => {
        await previousPromise;
        return Promise.all(batch.map(copro => setAdminValidation(copro.id, true, jwt)));
      }, Promise.resolve());

      // Step 6: Detect DPEs
      await fetch(`/api/companies/${company.id}/dpes/detect-new`, {
        method: 'POST',
        ...JWTHeader(jwt),
      });

      setStatus('success');
      showToast('success', 'Mode auto-demo terminé avec succès.');
    } catch (error) {
      console.error('Error during demo process :', error);
      showToast('error', 'Une erreur est survenue lors de la démo.');
      setStatus('idle');
    }
  };

  

  

  if (!isAdmin) {
    // althaugh we do not want to display the button for non admin users, we better check here to avoid any error
    return null;
  }

  return (
    <>
      <button
        onClick={handleDemoClick}
        disabled={status === 'loading' || status === 'success'}
        className="inline text-gray-500 hover:text-gray-700 hover:underline"
      >
        {status === 'idle' && 'Lancer la DEMO'}
        {status === 'loading' && (
          <>
            <Spinner size="sm" className="mr-2" />
            Chargement...
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircleIcon className="mr-2 inline h-5 w-5" />
            Import démo terminé
          </>
        )}
      </button>

      <Popup
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setIsCompanyAlreadyConfigured(false);
        }}
        title="Confirmation"
        width="400px"
      >
        {checkingConfiguration ? (
          <div className="flex items-center justify-center py-4">
            <Spinner size="sm" className="mr-2" />
            <span className="text-black">Vérification en cours…</span>
          </div>
        ) : isCompanyAlreadyConfigured ? (
          <>
            <div className="mb-6 flex flex-col items-center gap-3 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-amber-500" />
              <p className="text-black">Impossible de lancer la démo sur une entreprise déjà configurée.</p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  setIsCompanyAlreadyConfigured(false);
                }}
                className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
              >
                Fermer
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-6 text-center text-black">Validez-vous le lancement de l'automatisation DEMO ?</p>
            <div className="flex justify-between">
              <button
                onClick={() => setShowConfirmation(false)}
                className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
              >
                Non
              </button>
              <button
                onClick={handleConfirmDemo}
                className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
              >
                Oui
              </button>
            </div>
          </>
        )}
      </Popup>
    </>
  );
};

export default AdminAutoDemo;
